import type { EditableDataTableColumn } from "@/components/editable-data-table";
import type { DeliveryNoteItem, RequestItem } from "@/types";

export function formatQuantity(value?: number | null): string {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("es-AR", { maximumFractionDigits: 2 });
}

export function toEditableQuantity(value?: number | null): string {
  if (value === null || value === undefined) return "";
  return value.toString().replace(".", ",");
}

export function createItemColumns(
  canEditItems: boolean,
  availableRequestItems: RequestItem[] | null | undefined,
  canEditNoteQuantity = false,
  canEditReceivedQuantity = false,
  canEditUnit = false,
  canEditRequestItem = false,
  deliveryNoteStatus?: string,
  unitOptions: { label: string; value: string }[] = [{ label: "UN - Unidades", value: "UN" }],
): EditableDataTableColumn<DeliveryNoteItem>[] {
  const columns: EditableDataTableColumn<DeliveryNoteItem>[] = [];

  columns.push({
    id: "description_extracted",
    header: "Descripción",
    accessor: "description_extracted",
    className: "w-40",
    truncate: true,
    editable: canEditItems
      ? {
          type: "text",
          placeholder: "Descripción del ítem",
        }
      : undefined,
  });

  columns.push({
    id: "quantity_in_note",
    header: "Cant. Remito",
    className: "text-right",
    cellClassName: "text-right",
    renderCell: (row) => formatQuantity(row.quantity_in_note),
    editable: canEditNoteQuantity
      ? {
          type: "number",
          placeholder: "0",
          inputMode: "decimal",
          getValue: (row) => toEditableQuantity(row.quantity_in_note),
          onCommitTransform: (value) => {
            const normalized = value.replace(",", ".").trim();
            if (normalized === "") return "";
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : value;
          },
        }
      : undefined,
  });

  columns.push({
    id: "quantity_received",
    header: "Cant. Recibida",
    className: "text-right",
    // Color amarillo si hay diferencia y está en pending_review
    cellClassName: (row) => {
      const received = Number(row.quantity_received ?? 0);
      const inNote = Number(row.quantity_in_note ?? 0);
      const hasDifference = received !== inNote && inNote > 0;
      const shouldHighlight = deliveryNoteStatus === "pending_review" && hasDifference;

      return shouldHighlight
        ? "text-right bg-amber-100 dark:bg-amber-900/50"
        : "text-right";
    },
    renderCell: (row) => formatQuantity(row.quantity_received),
    editable: canEditReceivedQuantity
      ? {
          type: "number",
          placeholder: "0",
          inputMode: "decimal",
          getValue: (row) => toEditableQuantity(row.quantity_received),
          onCommitTransform: (value) => {
            const normalized = value.replace(",", ".").trim();
            if (normalized === "") return "";
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : value;
          },
        }
      : undefined,
  });

  columns.push({
    id: "unit",
    header: "Unidad",
    accessor: (row) => row.unit ?? "-",
    cellClassName: "text-muted-foreground",
    editable: canEditUnit
      ? {
          type: "async-combobox",
          placeholder: "Seleccionar unidad",
          loadOptions: (query: string) => {
            const normalizedQuery = query.toLowerCase().trim();
            if (!normalizedQuery) {
              return Promise.resolve(unitOptions);
            }
            return Promise.resolve(
              unitOptions.filter(
                (option) =>
                  option.label.toLowerCase().includes(normalizedQuery) ||
                  option.value.toLowerCase().includes(normalizedQuery)
              )
            );
          },
          getCurrentOption: (row) => {
            const currentUnit = row.unit;
            if (!currentUnit) return null;
            const option = unitOptions.find((opt) => opt.value === currentUnit);
            return option ?? { label: currentUnit, value: currentUnit };
          },
        }
      : undefined,
  });

  // Always show request_item column, but make it editable only when there's a purchase_request
  columns.push({
    id: "request_item_id",
    header: "Ítem de Solicitud",
    truncate: true,
    renderCell: (row) => {
      // Show matched item
      if (row.request_item) {
        return `#${row.request_item.item_number} - ${row.request_item.description}`;
      }

      // Show hint when no PR is linked
      const hasPurchaseRequest = availableRequestItems != null;
      if (!hasPurchaseRequest) {
        return <span className="text-muted-foreground text-xs">Sin solicitud vinculada</span>;
      }

      // Show unassigned state
      return "Sin asignar";
    },
    editable: canEditRequestItem && availableRequestItems
      ? {
          type: "async-combobox",
          placeholder: "Seleccionar ítem…",
          loadOptions: (query: string, _row: DeliveryNoteItem) => {
            // Local filtering on preloaded available_request_items
            const normalizedQuery = query.toLowerCase().trim();
            if (!normalizedQuery) {
              return Promise.resolve(availableRequestItems.map((ri) => ({
                value: ri.id,
                label: `#${ri.item_number} - ${ri.description}`,
              })));
            }

            return Promise.resolve(availableRequestItems
              .filter(
                (ri) =>
                  ri.item_number.toString().includes(normalizedQuery) ||
                  ri.description.toLowerCase().includes(normalizedQuery),
              )
              .map((ri) => ({
                value: ri.id,
                label: `#${ri.item_number} - ${ri.description}`,
              })));
          },
          getCurrentOption: (row) =>
            row.request_item
              ? {
                  value: row.request_item.id,
                  label: `#${row.request_item.item_number} - ${row.request_item.description}`,
                }
              : null,
        }
      : undefined,
  });

  return columns;
}
